import React, { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchBloodCatalogue } from "../../hooks/useBloodRangeContext";
import { hasFeature } from "../../utils/subscriptions/subscriptionService";
import { useSessionUser } from "../../utils/auth/sessionUser";
import { isEnrolmentConfined, useAuthSession } from "../../lib/http/session";
import { ENROLMENT_PATH } from "../../lib/http/stepup";
import SessionBootstrap from "./SessionBootstrap";

export type SessionGate = "login" | "wait" | "enrol" | "ready";

// The guard decides from identity's answer (GET /auth/session), never from a cookie JavaScript can read.
export const useSessionGate = (): SessionGate => {
	const { status, info } = useAuthSession();
	const profile = useSessionUser();
	if (status === "anonymous") return "login";
	if (status === "authenticated" && isEnrolmentConfined(info)) return "enrol";
	if (status !== "authenticated" || !profile) return "wait";
	return "ready";
};

// One guard for the whole app: authentication, the permission predicate, and the subscription feature, checked together.
const RouteGuard = ({
	allow,
	feature,
	redirectTo = "/",
	prefetchBlood = false,
	children,
}: {
	allow?: () => boolean;
	feature?: string | string[];
	redirectTo?: string;
	prefetchBlood?: boolean;
	children: JSX.Element;
}) => {
	const gate = useSessionGate();
	const queryClient = useQueryClient();
	// Blood surfaces resolve ranges against the catalogue; warming it here means they never open against an empty one
	useEffect(() => {
		if (gate === "ready" && prefetchBlood) void prefetchBloodCatalogue(queryClient);
	}, [gate, prefetchBlood, queryClient]);

	if (gate === "login") return <Navigate to="/login" replace />;
	if (gate === "enrol") return <Navigate to={ENROLMENT_PATH} replace />;
	if (gate === "wait") return <SessionBootstrap />;

	const allowed = allow ? allow() : true;
	// A list admits the route when any one of its features is on.
	const featureOk = !feature || (Array.isArray(feature) ? feature.some(hasFeature) : hasFeature(feature));
	return allowed && featureOk ? children : <Navigate to={redirectTo} replace />;
};

export default RouteGuard;
